import React from 'react';
import { useSelector } from 'react-redux';
import { Content, ContextualHelp, Flex, Heading, Text } from '@adobe/react-spectrum';

import { BaseDeckPane } from './BaseDeckPane';
import { getPrettyLabel } from '../../../data/CategoryPrettyDisplayUtils';
import {
    WOTC_FLOORED_HEADING,
    WOTC_FLOORED_NOTE_LEAD,
    WOTC_FLOORED_NOTE_BODY,
    WOTC_FLOORED_MARKER,
} from '../../../data/bracketFloorCopy';
import HeroPanel, { HeroPanelChip, HeroPanelTotal } from '../../common/heroPanel/HeroPanel';
import './bracketsPane.css';

const BRACKET_LABELS = {
    1: 'Exhibition',
    2: 'Core',
    3: 'Upgraded',
    4: 'Optimized',
    5: 'cEDH',
};

const BRACKET_TONE = {
    1: 'ok',
    2: 'ok',
    3: 'watch',
    4: 'alert',
    5: 'alert',
};

const INTENT_LEVELS = {
    1: { verdict: 'Exhibition — table-friendly', tone: 'ok' },
    2: { verdict: 'Casual — built for social play', tone: 'ok' },
    3: { verdict: 'Upgraded — focused casual', tone: 'watch' },
    4: { verdict: 'Optimized — tuned to win', tone: 'alert' },
    5: { verdict: 'Competitive — all-in', tone: 'alert' },
};

const formatPower = (p) => (typeof p === 'number' && !Number.isNaN(p) ? p.toFixed(1) : '—');
const plural = (n, single, many) => `${n} ${n === 1 ? single : many}`;

const WOTC_REASON_LABELS = {
    extraTurns: 'extra-turn spells',
    massLandDenial: 'mass land destruction',
    gameChangers: 'game-changers',
    earlyGameInfiniteCombos: 'early-game infinite combos',
    twoCardCombos: 'two-card combos',
};

const formatTriggeredReasons = (reasons) => {
    if (!Array.isArray(reasons) || reasons.length === 0) return 'WotC criteria';
    const labels = reasons.map((r) => WOTC_REASON_LABELS[r] || r);
    if (labels.length === 1) return labels[0];
    if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
    return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
};

const BRACKET_PROFILE_COPY = {
    rationale: {
        powerLevelExceedsWotcBracket: ({ powerLevel, targetBracket }) =>
            `Power-level rating of ${formatPower(powerLevel)} puts the deck in bracket ${targetBracket} territory on practical play.`,
        enablerComboCore: () =>
            'Dense enabler-combo core — every turn of setup compounds toward a win.',
        manabaseQuality: ({ density }) =>
            `Manabase quality is ${density} — on-color sources and fetch/dual density match higher-bracket expectations.`,
        winconDensity: ({ paths, winconCount, effectiveLines }) => {
            const hasEffective = typeof effectiveLines === 'number' && effectiveLines !== winconCount;
            const winconPhrase = hasEffective
                ? `${plural(effectiveLines, 'effective combo wincon line', 'effective combo wincon lines')} (of ${winconCount} listed)`
                : plural(
                      typeof effectiveLines === 'number' ? effectiveLines : winconCount,
                      'combo wincon',
                      'combo wincons'
                  );
            return `Win-condition density is strong — ${plural(paths, 'path', 'paths')}, ${winconPhrase}.`;
        },
        efficiencyHigh: ({ density }) =>
            `Efficiency is ${density} — fast mana + ramp density is tournament-comparable.`,
        tutorDensity: ({ density }) =>
            `Tutor density is ${density} — the deck finds its key pieces reliably.`,
        fastManaPackage: ({ density }) =>
            `Fast-mana density is ${density} — Sol Ring / Mana Crypt / Mox family territory.`,
        staxDensity: ({ density }) =>
            `Stax density is ${density} — this deck can shut the table down.`,
        interactionDensity: ({ density }) =>
            `Interaction density is ${density} — stack and board answers in quantity.`,
        comboRedundancy: ({ effectiveLines }) =>
            `Combo redundancy — ${plural(effectiveLines, 'distinct line', 'distinct lines')} for consistency.`,
        wotcCriteriaTriggered: ({ wotcBracket, csBracket, triggeredReasons }) =>
            `WotC criteria flag bracket ${wotcBracket} on ${formatTriggeredReasons(triggeredReasons)} — but the rest of the deck reads bracket ${csBracket}.`,
        comboRedundancyWeak: ({ effectiveLines, winconCount, comboCount }) =>
            `Combo wincons share pieces — ${plural(winconCount, 'wincon', 'wincons')} and ${plural(comboCount, 'combo', 'combos')} listed but only ${plural(effectiveLines, 'effective line', 'effective lines')}; one combo plan in multiple surface forms.`,
        competitiveIndicatorsLow: ({ fastMana, tutors }) =>
            `Competitive speed pillars are absent — fast-mana density is ${fastMana} and tutor density is ${tutors}, below what the WotC bracket implies.`,
        lowEfficiencyForBracket: ({ density, wotcBracket }) =>
            `Efficiency reads ${density} — short of bracket-${wotcBracket} pace.`,
        winConditionsThin: ({ paths, winconCount }) =>
            `Win conditions are thin — only ${plural(paths, 'path', 'paths')} and ${plural(winconCount, 'combo wincon', 'combo wincons')}; the trigger is incidental, not load-bearing.`,
        antiPatternActive: ({ severity }) =>
            `Anti-pattern detector is firing (${severity}) — pulls the practical read below the WotC trigger.`,
    },
    soften: {
        removeGameChangers: ({ count, targetBracket }) =>
            `Remove ${plural(count, 'game-changer', 'game-changers')} to exit bracket-${targetBracket} territory.`,
        cutExtraTurns: ({ count }) =>
            `Cut extra-turn spells — ${count} present triggers the bracket-4 criterion.`,
        removeMassLandDenial: ({ count }) =>
            `Remove mass land destruction (${count} present) — bracket-2 and bracket-3 decks don't run these.`,
        avoidEarlyGameCombos: ({ count }) =>
            `Avoid early-game infinite combos (${count} detected) if you want a softer read.`,
        thinEnablers: () =>
            'The enabler-combo core is dense — thin the enablers for a casual shell.',
        loosenStax: () =>
            'Dense stax density — loosen the lock pieces to land a bracket lower.',
        fewerTutors: () =>
            'Dense tutor density — cutting tutors softens the read significantly.',
    },
    harden: {
        tightenExecution: ({ powerLevel, threshold, targetBracket }) =>
            `Power level is ${formatPower(powerLevel)} — tightening execution toward ${threshold}+ pushes into bracket ${targetBracket}.`,
        addMassLandDenial: ({ targetBracket }) =>
            `Add mass land destruction — triggers the bracket-${targetBracket} criterion.`,
        addEarlyGameCombo: ({ targetBracket }) =>
            `Add an early-game infinite combo — triggers the bracket-${targetBracket} criterion.`,
        addGameChangers: ({ currentCount, targetCount, targetBracket }) =>
            `Run more game-changers (${currentCount} currently, ${targetCount}+ for bracket ${targetBracket}).`,
        addCombos: () =>
            'Add a combo package — currently no combos registered.',
        addTutors: ({ currentDensity, targetBracket }) =>
            `Tutor density is ${currentDensity} — adding tutors improves consistency toward bracket ${targetBracket}.`,
        addFastMana: ({ currentDensity, targetBracket }) =>
            `Fast-mana density is ${currentDensity} — more fast mana reaches bracket-${targetBracket} execution.`,
        addInteraction: ({ currentDensity, targetBracket }) =>
            `Interaction density is ${currentDensity} — more answers reach bracket-${targetBracket} resilience.`,
        upgradeManabase: ({ currentDensity, targetBracket }) =>
            `Manabase quality is ${currentDensity} — upgrades (fetches, duals) are expected at bracket ${targetBracket}.`,
    },
    ruleZero: {
        highTutorDensity: () =>
            'High tutor density — the deck finds its key pieces much more reliably than a typical mid-bracket build.',
        fastManaPackage: () =>
            'Fast-mana package present — expect faster starts than the bracket suggests.',
        staxPresent: () =>
            'Stax pieces present — board actions will be taxed or denied.',
        comboRedundancy: ({ effectiveLines, winconCount }) =>
            `Combo redundancy — ${plural(effectiveLines, 'line', 'lines')}, ${plural(winconCount, 'combo wincon', 'combo wincons')}. Plan for multi-combo resilience.`,
        antiPatternFlagged: ({ severity }) =>
            `Flagged for mechanical anti-patterns (${severity}). Worth disclosing before game one.`,
        twoCardCombo: ({ count }) => {
            const n = Number(count);
            const many = Number.isFinite(n) && n > 1;
            const lead = many ? `This deck has ${n} two-card combos` : 'This deck has a two-card combo';
            return `${lead} — disclose ${many ? 'them' : 'it'} before game one. Two-card combos place the deck at bracket 3 or higher.`;
        },
    },
};

function renderProfileItems(items, category) {
    const map = BRACKET_PROFILE_COPY[category] || {};
    return (items || [])
        .map((item) => {
            const fn = map[item?.id];
            if (!fn) return null;
            try {
                const text = fn(item.data || {});
                if (!text) return null;
                const direction = item?.direction === 'down' ? 'down' : 'up';
                return { id: item.id, text, direction };
            } catch (err) {
                if (typeof console !== 'undefined' && console.warn) {
                    console.warn(`BracketsPane: failed to render ${category}.${item?.id}`, err);
                }
                return null;
            }
        })
        .filter(Boolean);
}

function computeCompetitiveIntent(wotcBracket, powerLevelRating) {
    if (wotcBracket === 5) return 5;
    const p = Number(powerLevelRating);
    if (Number.isNaN(p)) return 1;
    if (p >= 9) return 5;
    if (p >= 7) return 4;
    if (p >= 5) return 3;
    if (p >= 3) return 2;
    return 1;
}

const WOTC_CATEGORY_META = {
    gameChangers: {
        label: 'Game Changers',
        help: 'Cards on Wizards\' game-changer list. 0 — none; 1–3 — bracket 3; 4+ — bracket 4.',
        tiers: [
            { max: 0, bracket: 2, verdict: 'None — core safe', tone: 'ok' },
            { max: 3, bracket: 3, verdict: 'A few — upgraded tier', tone: 'watch' },
            { max: Infinity, bracket: 4, verdict: 'Dense — optimized tier', tone: 'alert' },
        ],
    },
    massLandDenial: {
        label: 'Mass Land Denial',
        help: 'Symmetrical mass land destruction (e.g. Armageddon). Any inclusion bumps the deck to bracket 4.',
        tiers: [
            { max: 0, bracket: 2, verdict: 'None — core safe', tone: 'ok' },
            { max: Infinity, bracket: 4, verdict: 'Present — optimized tier', tone: 'alert' },
        ],
    },
    extraTurns: {
        label: 'Extra Turns',
        help: 'Extra-turn spells. 0 — none; <5 — bracket 3; 5+ — bracket 4.',
        tiers: [
            { max: 0, bracket: 2, verdict: 'None — core safe', tone: 'ok' },
            { max: 4, bracket: 3, verdict: 'Splash — upgraded tier', tone: 'watch' },
            { max: Infinity, bracket: 4, verdict: 'Chainable — optimized tier', tone: 'alert' },
        ],
    },
    twoCardCombos: {
        label: 'Two-Card Combos',
        help: 'Clean two-card wincon, lock, or infinite combos — exactly ≤2 cards, needing no extra cards and nothing already on board. Any inclusion floors the deck at bracket 3.',
        tiers: [
            { max: 0, bracket: 2, verdict: 'None — core safe', tone: 'ok' },
            { max: Infinity, bracket: 3, verdict: 'Present — upgraded tier', tone: 'watch' },
        ],
    },
    earlyGameInfiniteCombos: {
        label: 'Early-Game Infinite Combos',
        help: 'Two-card combos that go infinite or win with ≤7 total mana. Any presence bumps the deck to bracket 4.',
        tiers: [
            { max: 0, bracket: 2, verdict: 'None — core safe', tone: 'ok' },
            { max: Infinity, bracket: 4, verdict: 'Present — optimized tier', tone: 'alert' },
        ],
    },
};

function bracketTierFor(categoryKey, count) {
    const meta = WOTC_CATEGORY_META[categoryKey];
    if (!meta) return null;
    for (const tier of meta.tiers) {
        if (count <= tier.max) return tier;
    }
    return meta.tiers[meta.tiers.length - 1];
}

function WotcCriteriaRow({ categoryKey, count, isReason }) {
    const meta = WOTC_CATEGORY_META[categoryKey];
    if (!meta) return null;
    const tier = bracketTierFor(categoryKey, count);
    const isPush = isReason || tier.bracket >= 4;

    return (
        <div
            className={`wotcCriteriaRow wotcCriteriaRow--${tier.tone}${isPush ? ' is-pushing' : ''}`}
            title={meta.help}
        >
            <span className={`wotcCriteriaDot wotcCriteriaDot--${tier.tone}`} aria-hidden="true" />
            <span className="wotcCriteriaLabel">{meta.label}</span>
            <span className="wotcCriteriaVerdict">{tier.verdict}</span>
            <span className="wotcCriteriaCount">{count}</span>
            <span className="wotcCriteriaTier">B{tier.bracket}</span>
        </div>
    );
}

function CompetitiveIntentRow({ intent }) {
    const meta = INTENT_LEVELS[intent] || INTENT_LEVELS[1];
    const isPush = intent >= 4;
    return (
        <div
            className={`wotcCriteriaRow wotcCriteriaRow--${meta.tone}${isPush ? ' is-pushing' : ''}`}
            title="Scaled from the deck's power-level rating — indicates how optimized the build plays at the table."
        >
            <span className={`wotcCriteriaDot wotcCriteriaDot--${meta.tone}`} aria-hidden="true" />
            <span className="wotcCriteriaLabel">Competitive Intent</span>
            <span className="wotcCriteriaVerdict">{meta.verdict}</span>
            <span className="wotcCriteriaCount">{intent}</span>
            <span className="wotcCriteriaTier">B{intent}</span>
        </div>
    );
}

export function BracketsPane() {
    const deck = useSelector((state) => state.deck.deck);
    const cards = useSelector((state) => state.deck.cards);

    const [bracketProfileOpen, setBracketProfileOpen] = React.useState(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
        return window.matchMedia('(min-width: 768px)').matches;
    });

    const getCategories = () => {
        const list = {};

        Object.keys(deck?.details?.brackets?.categories || {})
            ?.filter((category) => {
                switch (category) {
                    case 'tutors':
                    case 'restock':
                        return false;
                    case 'cedhStaples':
                        return deck?.details?.brackets?.wotcBracket === 5;
                    default:
                        return true;
                }
            })
            ?.map((category) => {
                let subCategoryList = {};
                let items = [];
                let catObjectKey = category === 'earlyGameInfiniteCombos' ? 'combos' : category;
                let label = getPrettyLabel(catObjectKey);

                if (category === 'earlyGameInfiniteCombos') {
                    subCategoryList[category] = {
                        label: `${getPrettyLabel(category)}`,
                        id: category,
                        count: deck.details.brackets.categories[category].list.length,
                        list:
                            deck.details.brackets.categories[category].list.map((_key) => {
                                let source = null;
                                deck?.details?.combos?.list?.forEach((combo) => {
                                    if (combo.id === _key) {
                                        source = combo;
                                    }
                                });

                                if (source) {
                                    return {
                                        id: _key,
                                        type: 'combo',
                                        cards: (source?.cards || []).map((__card) => {
                                            if (cards.hasOwnProperty(__card)) {
                                                return cards?.[__card];
                                            }

                                            let retCard = null;
                                            Object.keys(cards || {}).forEach((cardId) => {
                                                if (cards[cardId].containerId === __card) {
                                                    if (cards[cardId].isFrontFace) {
                                                        retCard = cards[cardId];
                                                    } else {
                                                        if (!retCard) {
                                                            if (new RegExp(`_${__card}`).test(cardId)) {
                                                                retCard = cards[cardId];
                                                            } else {
                                                                if (new RegExp(`${__card}_back`).test(cardId)) {
                                                                    retCard = cards[cardId];
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            });
                                            return retCard;
                                        }),
                                    };
                                }
                            }) || [],
                        parentCategory: catObjectKey,
                    };

                    if (list.hasOwnProperty(catObjectKey)) {
                        list[catObjectKey].subCategories = {
                            ...list[catObjectKey].subCategories,
                            ...subCategoryList,
                        };
                        list[catObjectKey].list = Array.from(
                            new Set(list[catObjectKey].list.concat(subCategoryList[category].list))
                        );

                        const foundIds = [];
                        list[catObjectKey].list = list[catObjectKey].list.filter((item) => {
                            if (!foundIds.includes(item.id)) {
                                foundIds.push(item.id);
                                return true;
                            }

                            return false;
                        });

                        list[catObjectKey].count = list[catObjectKey].list.length;
                    } else {
                        items = items.concat(subCategoryList[category].list);
                    }
                } else {
                    items = deck.details.brackets.categories[category].list.map((item) => {
                        if (!cards?.hasOwnProperty(item)) {
                            let source = null;
                            deck?.details?.combos?.list?.forEach((combo) => {
                                if (combo.id === item) {
                                    source = combo;
                                }
                            });

                            if (source) {
                                return {
                                    id: item,
                                    type: 'combo',
                                    cards: (source?.cards || []).map((__card) => {
                                        if (cards.hasOwnProperty(__card)) {
                                            return cards?.[__card];
                                        }

                                        let retCard = null;
                                        Object.keys(cards || {}).forEach((cardId) => {
                                            if (cards[cardId].containerId === __card) {
                                                if (cards[cardId].isFrontFace) {
                                                    retCard = cards[cardId];
                                                } else {
                                                    if (!retCard) {
                                                        if (new RegExp(`_${__card}`).test(cardId)) {
                                                            retCard = cards[cardId];
                                                        } else {
                                                            if (new RegExp(`${__card}_back`).test(cardId)) {
                                                                retCard = cards[cardId];
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        });
                                        return retCard;
                                    }),
                                };
                            }
                        }

                        return {
                            ...item,
                            ...cards?.[item],
                        };
                    });
                }

                if (!list.hasOwnProperty(catObjectKey)) {
                    list[catObjectKey] = {
                        label: `${label}`,
                        id: catObjectKey,
                        count: items?.length || 0,
                        list: items,
                        color: `#FFFFFF`,
                        subCategories: subCategoryList,
                    };
                }
            });

        Object.keys(list).forEach((key) => {
            if (list?.[key]?.list?.length < 1) {
                delete list?.[key];
            } else {
                Object.keys(list?.[key]?.subCategories).forEach((subKey) => {
                    if (list?.[key]?.subCategories?.[subKey]?.list?.length < 1) {
                        delete list?.[key]?.subCategories?.[subKey];
                    }
                });
            }
        });

        return list;
    };

    const brackets = deck?.details?.brackets || {};
    const wotcBracket = brackets.wotcBracket;
    const csBracket = brackets.csBracket;
    const bracketCategories = brackets.categories || {};
    const reasons = brackets.reasons || {};
    // No bracket-defining signals at all — the case that used to read Bracket 1.
    // The API now floors it to a WotC Bracket 2 and flags it, since Exhibition-
    // style decks can't be detected mechanically.
    const wotcBracketFloored = brackets.wotcBracketFloored === true;

    const profile = brackets.profile || {};
    const showSuggestions = brackets.showSuggestions !== false;

    const bracketDelta = csBracket - wotcBracket;
    const deltaVerdict =
        bracketDelta < 0
            ? 'Softer than the criteria suggest — deck underperforms its WotC bracket.'
            : bracketDelta > 0
            ? 'Hits above the criteria suggest — deck outperforms its WotC bracket.'
            : 'Matches the criteria on both strict and practical reads.';

    const wotcCategoryKeys = [
        'gameChangers',
        'massLandDenial',
        'extraTurns',
        'twoCardCombos',
        'earlyGameInfiniteCombos',
    ];

    const finalRationale = renderProfileItems(profile.rationale, 'rationale');
    const finalSoften = renderProfileItems(profile.soften, 'soften');
    const finalHarden = renderProfileItems(profile.harden, 'harden');
    const finalRuleZero = renderProfileItems(profile.ruleZero, 'ruleZero');

    if (finalRationale.length === 0) {
        finalRationale.push({
            id: '__fallback',
            text: `Power level and shape-detection both land on bracket ${csBracket} — no major softening or hardening adjustments applied.`,
            direction: 'flat',
        });
    }

    return (
        <BaseDeckPane
            title={'Bracket Assessment'}
            fieldName="name"
            content={<div />}
            showScore={false}
            categories={getCategories()}
        >
            <Flex direction="column" gap="size-200">
                <HeroPanel
                    tone="blue"
                    eyebrow="Bracket Assessment"
                    title={BRACKET_LABELS[csBracket] || '—'}
                    hint={
                        <>
                            {deltaVerdict}
                            <ContextualHelp variant="help">
                                <Heading>WotC vs. Commandersalt</Heading>
                                <Content>
                                    <Text>
                                        The WotC bracket applies the strict criteria listed in the
                                        Brackets announcement (game changers, MLD, extra turns, and
                                        early-game infinite combos). The Commandersalt bracket softens
                                        that read using deck realities — manabase quality, synergy
                                        density, win-con reliability, and compression patterns — to
                                        estimate how the deck actually plays at table.
                                    </Text>
                                </Content>
                            </ContextualHelp>
                        </>
                    }
                    aside={<HeroPanelTotal label="Commandersalt" value={csBracket ?? '—'} unit="/ 5" />}
                >
                    <HeroPanelChip
                        accent="warm"
                        label="WotC baseline"
                        value={
                            wotcBracketFloored ? (
                                <>
                                    {`B${wotcBracket}`}
                                    <span className="bracketFloorStar">{WOTC_FLOORED_MARKER}</span>
                                    {` · ${BRACKET_LABELS[wotcBracket] || '—'}`}
                                    <ContextualHelp variant="info">
                                        <Heading>{WOTC_FLOORED_HEADING}</Heading>
                                        <Content>
                                            <Text>
                                                {WOTC_FLOORED_NOTE_LEAD}
                                                <p />
                                                {WOTC_FLOORED_NOTE_BODY}
                                            </Text>
                                        </Content>
                                    </ContextualHelp>
                                </>
                            ) : (
                                `B${wotcBracket ?? '—'} · ${BRACKET_LABELS[wotcBracket] || '—'}`
                            )
                        }
                    />
                    <HeroPanelChip
                        accent={bracketDelta > 0 ? 'up' : bracketDelta < 0 ? 'down' : 'flat'}
                        label="Delta"
                        value={`${bracketDelta > 0 ? '↗' : bracketDelta < 0 ? '↘' : '═'} ${
                            bracketDelta === 0
                                ? 'in sync'
                                : `${bracketDelta > 0 ? '+' : ''}${bracketDelta}`
                        }`}
                    />
                </HeroPanel>

                <section className={`bracketSection bracketSection--collapsible${bracketProfileOpen ? ' is-open' : ''}`}>
                    <button
                        type="button"
                        className="bracketSectionToggle"
                        aria-expanded={bracketProfileOpen}
                        aria-controls="bracketProfileBody"
                        onClick={() => setBracketProfileOpen((v) => !v)}
                    >
                        <span className="bracketSectionEyebrow">
                            <span className="bracketSectionChevron" aria-hidden="true">
                                {bracketProfileOpen ? '▾' : '▸'}
                            </span>
                            Bracket profile
                        </span>
                        <span className="bracketSectionHint">
                            Criteria checklist, rating rationale, and verdict & suggestions — why the
                            deck lands where it does and what to change to shift it.
                        </span>
                    </button>

                    {bracketProfileOpen && (
                        <div id="bracketProfileBody" className="bracketSectionBody">
                            <div className="bracketSubSection">
                                <header className="bracketSubSectionHeader">
                                    <span className="bracketSectionEyebrowRow">
                                        <span className="bracketSectionScopeTag bracketSectionScopeTag--wotc">
                                            WotC
                                        </span>
                                        <span className="bracketSubSectionEyebrow">Criteria checklist</span>
                                    </span>
                                    <span className="bracketSectionHint">
                                        Each checks a specific Wizards-defined category. These drive the WotC
                                        bracket directly — they do not affect the Commandersalt rating.
                                    </span>
                                </header>
                                <div className="wotcCriteriaList">
                                    {wotcCategoryKeys.map((k) => (
                                        <WotcCriteriaRow
                                            key={k}
                                            categoryKey={k}
                                            count={bracketCategories[k]?.count ?? 0}
                                            isReason={!!reasons[k]}
                                        />
                                    ))}
                                    {wotcBracket === 5 && (
                                        <CompetitiveIntentRow
                                            intent={computeCompetitiveIntent(
                                                wotcBracket,
                                                deck?.powerLevelRating
                                            )}
                                        />
                                    )}
                                </div>
                                {wotcBracketFloored && (
                                    <div className="wotcFlooredNote">
                                        <p>{WOTC_FLOORED_NOTE_LEAD}</p>
                                        <p>{WOTC_FLOORED_NOTE_BODY}</p>
                                    </div>
                                )}
                            </div>

                            <div className="bracketSubSection">
                                <header className="bracketSubSectionHeader">
                                    <span className="bracketSectionEyebrowRow">
                                        <span className="bracketSectionScopeTag bracketSectionScopeTag--cs">
                                            Commandersalt
                                        </span>
                                        <span className="bracketSubSectionEyebrow">Rating rationale</span>
                                        <ContextualHelp variant="help">
                                            <Heading>How the Commandersalt bracket is derived</Heading>
                                            <Content>
                                                <Text>
                                                    The Commandersalt bracket starts from the deck's overall
                                                    power-level rating, then adjusts up or down based on shape
                                                    signals the WotC criteria don't capture — manabase quality,
                                                    tutor density, interaction density, wincon density, fast
                                                    mana, and combo redundancy.
                                                    <p />
                                                    The rationale below lists the mechanical signals that moved
                                                    the read for this specific deck, pulled from the bracket
                                                    profile returned by the API.
                                                </Text>
                                            </Content>
                                        </ContextualHelp>
                                    </span>
                                    <span className="bracketSectionHint">
                                        {bracketDelta < 0
                                            ? `Why the deck lands on bracket ${csBracket} despite the WotC criteria flagging bracket ${wotcBracket}.`
                                            : bracketDelta > 0
                                            ? `Why the deck plays at bracket ${csBracket} even though the strict WotC criteria only reach bracket ${wotcBracket}.`
                                            : `Why both reads agree on bracket ${csBracket}.`}
                                    </span>
                                </header>
                                <div className="bracketRationaleList">
                                    {finalRationale.map((r, idx) => {
                                        const tone = r.direction || 'up';
                                        return (
                                            <div
                                                className={`bracketRationaleRow bracketRationaleRow--${tone}`}
                                                key={`rationale_${r.id}_${idx}`}
                                            >
                                                <span className="bracketRationaleArrow" aria-hidden="true">
                                                    {tone === 'down' ? '↘' : tone === 'up' ? '↗' : '═'}
                                                </span>
                                                <span className="bracketRationaleText">{r.text}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="bracketSubSection">
                                <header className="bracketSubSectionHeader">
                                    <span className="bracketSubSectionEyebrow">Verdict & Suggestions</span>
                                    <span className="bracketSectionHint">
                                        Concrete moves to shift this deck toward or away from its current
                                        bracket, plus pre-game disclosures worth surfacing at the table.
                                    </span>
                                </header>
                                <div className="bracketSuggestionsGrid">
                                    {showSuggestions && finalSoften.length > 0 && (
                                        <div className="bracketSuggestionCard bracketSuggestionCard--down">
                                            <span className="bracketSuggestionTitle">
                                                <span className="bracketSuggestionArrow" aria-hidden="true">
                                                    ↓
                                                </span>
                                                Drop to a lower bracket
                                                <span className="bracketSuggestionCount">
                                                    {finalSoften.length}
                                                </span>
                                            </span>
                                            <p className="bracketSuggestionCopy">
                                                Changes that would pull the deck's practical read{' '}
                                                <b>down from bracket {csBracket}</b> toward a softer table.
                                            </p>
                                            <ul className="bracketSuggestionList">
                                                {finalSoften.map((s) => (
                                                    <li key={`soften_${s.id}`}>{s.text}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {showSuggestions && finalHarden.length > 0 && (
                                        <div className="bracketSuggestionCard bracketSuggestionCard--up">
                                            <span className="bracketSuggestionTitle">
                                                <span className="bracketSuggestionArrow" aria-hidden="true">
                                                    ↑
                                                </span>
                                                Push to a higher bracket
                                                <span className="bracketSuggestionCount">
                                                    {finalHarden.length}
                                                </span>
                                            </span>
                                            <p className="bracketSuggestionCopy">
                                                Changes that would push the deck's practical read{' '}
                                                <b>up from bracket {csBracket}</b> toward a harder table.
                                            </p>
                                            <ul className="bracketSuggestionList">
                                                {finalHarden.map((s) => (
                                                    <li key={`harden_${s.id}`}>{s.text}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {finalRuleZero.length > 0 && (
                                        <div className="bracketSuggestionCard bracketSuggestionCard--over">
                                            <span className="bracketSuggestionTitle">
                                                Rule-zero notes
                                                <span className="bracketSuggestionCount">
                                                    {finalRuleZero.length}
                                                </span>
                                            </span>
                                            <p className="bracketSuggestionCopy">
                                                Pre-game disclosures — worth surfacing before game one so the
                                                table isn't blindsided.
                                            </p>
                                            <ul className="bracketSuggestionList">
                                                {finalRuleZero.map((s) => (
                                                    <li key={`rz_${s.id}`}>{s.text}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {csBracket === wotcBracket &&
                                        finalHarden.length === 0 &&
                                        finalSoften.length === 0 && (
                                            <div className="bracketSuggestionCard bracketSuggestionCard--match">
                                                <span className="bracketSuggestionTitle">Verdict</span>
                                                <p className="bracketSuggestionCopy">
                                                    The system works. This reads as a solid bracket{' '}
                                                    <b>{csBracket}</b> deck on both axes, with no material
                                                    weaknesses or over-performing patterns detected.
                                                </p>
                                            </div>
                                        )}
                                </div>
                            </div>
                        </div>
                    )}
                </section>
            </Flex>
        </BaseDeckPane>
    );
}
